// src/admin/AdminAjudaPontuacao.js
import React, { useEffect, useRef, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import DOMPurify from "dompurify";

// ⚠️ Ajusta esses caminhos se o teu projeto estiver organizado diferente
import { categoriasPorModo } from "../services/estruturaCategorias";
import nomesBonitosCategoria from "../utils/nomesBonitosCategoria";

/* ============ TABELA PADRÃO GERADA PELOS MODOS/CATEGORIAS ============ */

/**
 * Mapeia o prefixo do modo para o nome bonitinho da premiação.
 * Ex.: "criticschoice-indicados" -> "CRITICS CHOICE – Indicados – ..."
 */
const LABEL_PREMIACAO = {
  oscar: "OSCAR",
  globodeouro: "GLOBO DE OURO",
  bafta: "BAFTA",
  sagawards: "SAG AWARDS",
  criticschoice: "CRITICS CHOICE",
  pga: "PGA",
  dga: "DGA",
};

const LABEL_TIPO = {
  indicados: "Indicados",
  vencedores: "Vencedores",
};

/**
 * Usa categoriasPorModo + nomesBonitosCategoria para montar
 * uma linha por (premiação + tipo + categoria), ex:
 * "OSCAR – Indicados – Melhor Filme"
 */
const DEFAULT_TABELA_PONTOS = (() => {
  const linhas = [];

  // categoriasPorModo esperado como:
  // {
  //   "oscar-indicados": ["melhorfilme", "melhordirecao", ...],
  //   "criticschoice-indicados": [...],
  //   ...
  // }
  Object.entries(categoriasPorModo || {}).forEach(
    ([modoId, listaCategorias]) => {
      const [premiacaoKey, tipoKey] = modoId.split("-");
      const premiacaoLabel =
        LABEL_PREMIACAO[premiacaoKey] || premiacaoKey?.toUpperCase() || "";
      const tipoLabel = LABEL_TIPO[tipoKey] || (tipoKey ? tipoKey : "");

      (listaCategorias || []).forEach((catId) => {
        const catLabel = nomesBonitosCategoria[catId] || catId;

        linhas.push({
          aposta: `${premiacaoLabel} – ${tipoLabel} – ${catLabel}`,
          acerto: "A definir",
          erro: "A definir",
        });
      });
    }
  );

  return linhas;
})();

/* ============ HELPERS UI ============ */
function Btn({ onClick, title, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        background: "#222",
        color: "#eee",
        border: "1px solid #333",
        borderRadius: 8,
        padding: "6px 10px",
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
function Swatch({ color, onClick, title }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={{
        width: 22,
        height: 22,
        borderRadius: 6,
        border: "1px solid #333",
        background: color,
        cursor: "pointer",
      }}
    />
  );
}

/* Aplica <span style="...">...</span> à seleção */
function applyInlineStyle(styleObj) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  if (range.collapsed) return;

  const wrapper = document.createElement("span");
  Object.assign(wrapper.style, styleObj);
  const frag = range.extractContents();
  wrapper.appendChild(frag);
  range.insertNode(wrapper);

  sel.removeAllRanges();
  const newRange = document.createRange();
  newRange.selectNodeContents(wrapper);
  sel.addRange(newRange);
}

/* ============ EDITOR RICO (contentEditable) ============ */
function EditorRico({ value, onChange }) {
  const areaRef = useRef(null);

  // Faz Enter criar <p> em vez de <div> (melhor pra consistência)
  useEffect(() => {
    try {
      document.execCommand("defaultParagraphSeparator", false, "p");
    } catch {}
  }, []);

  // Mantém o HTML do editor em sincronia com o state externo
  useEffect(() => {
    if (areaRef.current && areaRef.current.innerHTML !== value) {
      areaRef.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    areaRef.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(areaRef.current?.innerHTML || "");
  };

  const onInput = (e) => onChange(e.currentTarget.innerHTML);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      // Garante quebra como parágrafo em todos os navegadores
      e.preventDefault();
      exec("insertParagraph");
    }
    // Shift+Enter deixa <br> padrão
  };

  const inserirLink = () => {
    const url = prompt("Insira o link (https://…):", "https://");
    if (!url) return;
    exec("createLink", url);
    // Ajusta links recém-criados
    Array.from(areaRef.current.querySelectorAll("a")).forEach((a) => {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
    });
    onChange(areaRef.current.innerHTML);
  };

  const limpar = () => {
    exec("removeFormat");
    exec("unlink");
    // Remove spans sem style
    areaRef.current
      ?.querySelectorAll("span")
      .forEach((s) => !s.getAttribute("style") && s.replaceWith(...s.childNodes));
    onChange(areaRef.current.innerHTML);
  };

  const aumentar = () => applyInlineStyle({ fontSize: "larger" });
  const diminuir = () => applyInlineStyle({ fontSize: "smaller" });

  const setColor = (c) => applyInlineStyle({ color: c });

  // Cola limpa (sanitiza conteúdo da área colada)
  const onPaste = (e) => {
    e.preventDefault();
    const html =
      e.clipboardData.getData("text/html") ||
      e.clipboardData.getData("text/plain");
    const clean = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "em",
        "u",
        "span",
        "div",
        "h1",
        "h2",
        "h3",
        "ul",
        "ol",
        "li",
        "a",
        "blockquote",
      ],
      ALLOWED_ATTR: ["style", "href", "target", "rel"],
    });
    document.execCommand("insertHTML", false, clean);
    onChange(areaRef.current?.innerHTML || "");
  };

  return (
    <div>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 8,
          alignItems: "center",
        }}
      >
        <Btn onClick={() => exec("bold")} title="Negrito (Ctrl+B)">
          B
        </Btn>
        <Btn onClick={() => exec("italic")} title="Itálico (Ctrl+I)">
          <em>I</em>
        </Btn>
        <Btn onClick={() => exec("underline")} title="Sublinhar (Ctrl+U)">
          <u>U</u>
        </Btn>

        <Btn onClick={() => exec("formatBlock", "H2")} title="Título H2">
          H2
        </Btn>
        <Btn
          onClick={() => exec("insertUnorderedList")}
          title="Lista com marcadores"
        >
          • Lista
        </Btn>
        <Btn
          onClick={() => exec("insertOrderedList")}
          title="Lista numerada"
        >
          1. Lista
        </Btn>

        <Btn onClick={() => exec("justifyLeft")} title="Alinhar à esquerda">
          ⟸
        </Btn>
        <Btn onClick={() => exec("justifyCenter")} title="Centralizar">
          ⇔
        </Btn>
        <Btn onClick={() => exec("justifyRight")} title="Alinhar à direita">
          ⟹
        </Btn>
        <Btn onClick={() => exec("justifyFull")} title="Justificar">
          ≡
        </Btn>

        <Btn onClick={inserirLink} title="Inserir link">
          Link
        </Btn>

        <Btn onClick={aumentar} title="Aumentar fonte">
          A+
        </Btn>
        <Btn onClick={diminuir} title="Diminuir fonte">
          A−
        </Btn>

        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            marginLeft: 4,
          }}
        >
          <span style={{ fontSize: 12, color: "#aaa" }}>Cor:</span>
          <Swatch
            color="#ffc00d"
            title="Amarelo"
            onClick={() => setColor("#ffc00d")}
          />
          <Swatch
            color="#ffffff"
            title="Branco"
            onClick={() => setColor("#ffffff")}
          />
          <Swatch
            color="#e53a2e"
            title="Vermelho"
            onClick={() => setColor("#e53a2e")}
          />
        </div>

        <Btn onClick={limpar} title="Limpar formatação">
          Limpar
        </Btn>
      </div>

      {/* Área editável */}
      <div
        ref={areaRef}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        style={{
          minHeight: 160,
          background: "#121212",
          border: "1px solid #2a2a2a",
          borderRadius: 10,
          padding: 12,
          color: "#eee",
          fontFamily: "Poppins, Arial, sans-serif",
          lineHeight: 1.7,
          outline: "none",
        }}
        dangerouslySetInnerHTML={{ __html: value || "" }}
      />
      {/* Regras visuais pro conteúdo dentro do editor */}
      <style>
        {`
        [contenteditable="true"] p { margin: 0 0 10px; }
        [contenteditable="true"] h2 { margin: 6px 0 8px; font-size: 20px; }
        [contenteditable="true"] ul { list-style: disc; padding-left: 1.25rem; margin: 6px 0; }
        [contenteditable="true"] ol { list-style: decimal; padding-left: 1.25rem; margin: 6px 0; }
        [contenteditable="true"] a { color: #4ea0ff; text-decoration: underline; }
        `}
      </style>
    </div>
  );
}

/* ============ PÁGINA ADMIN ============ */
export default function AdminAjudaPontuacao() {
  const [carregando, setCarregando] = useState(true);
  const [comoFuncionaHtml, setComoFuncionaHtml] = useState("");
  const [tabelaPontuacao, setTabelaPontuacao] = useState(DEFAULT_TABELA_PONTOS);

  useEffect(() => {
    (async () => {
      setCarregando(true);
      try {
        const ref = doc(db, "appConteudo", "ajudaTermometeiro");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          setComoFuncionaHtml(data.comoFuncionaHtml || "");
          setTabelaPontuacao(
            Array.isArray(data.tabelaPontuacao) &&
              data.tabelaPontuacao.length
              ? data.tabelaPontuacao
              : DEFAULT_TABELA_PONTOS
          );
        } else {
          setComoFuncionaHtml("");
          setTabelaPontuacao(DEFAULT_TABELA_PONTOS);
        }
      } catch (e) {
        console.error("Erro ao carregar ajudaTermometeiro:", e);
        setComoFuncionaHtml("");
        setTabelaPontuacao(DEFAULT_TABELA_PONTOS);
      } finally {
        setCarregando(false);
      }
    })();
  }, []);

  async function salvar() {
    try {
      const clean = DOMPurify.sanitize(comoFuncionaHtml, {
        ALLOWED_TAGS: [
          "p",
          "br",
          "strong",
          "em",
          "u",
          "span",
          "div",
          "h1",
          "h2",
          "h3",
          "ul",
          "ol",
          "li",
          "a",
          "blockquote",
          "table",
          "thead",
          "tbody",
          "tr",
          "th",
          "td",
        ],
        ALLOWED_ATTR: ["style", "href", "target", "rel"],
      });

      const ref = doc(db, "appConteudo", "ajudaTermometeiro");
      await setDoc(
        ref,
        { comoFuncionaHtml: clean, tabelaPontuacao },
        { merge: true }
      );
      alert("Conteúdo salvo com sucesso!");
    } catch (e) {
      console.error("Erro ao salvar ajudaTermometeiro:", e);
      alert("Erro ao salvar. Veja o console para detalhes.");
    }
  }

  if (carregando) return <p>A carregar conteúdo…</p>;

  return (
    <div className="painel-ajuda" style={{ marginTop: 16 }}>
      <h2>Ajuda do Termometeiro</h2>

      {/* COMO FUNCIONA */}
      <div style={{ marginBottom: 16 }}>
        <label
          style={{
            display: "block",
            fontWeight: 700,
            margin: "8px 0 6px",
          }}
        >
          Como funciona (editor de texto rico)
        </label>

        <EditorRico value={comoFuncionaHtml} onChange={setComoFuncionaHtml} />

        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 8,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={() => setComoFuncionaHtml("")}
            style={{
              border: "1px solid #333",
              background: "transparent",
              color: "#eee",
              borderRadius: 8,
              padding: "8px 12px",
              cursor: "pointer",
            }}
          >
            Limpar texto
          </button>

          <button className="btn" onClick={salvar}>
            Salvar
          </button>
        </div>
      </div>

      {/* PREVIEW (igual ao ProfilePage) */}
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            fontWeight: 700,
            margin: "8px 0 6px",
          }}
        >
          Pré-visualização (como aparece no perfil)
        </div>
        <div
          className="rich-html"
          style={{
            background: "#0f0f0f",
            border: "1px solid #2a2a2a",
            borderRadius: 10,
            padding: 14,
            color: "#eaeaea",
            lineHeight: 1.8,
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(comoFuncionaHtml || "<p>—</p>", {
              ALLOWED_TAGS: [
                "p",
                "br",
                "strong",
                "em",
                "u",
                "span",
                "div",
                "h1",
                "h2",
                "h3",
                "ul",
                "ol",
                "li",
                "a",
                "blockquote",
                "table",
                "thead",
                "tbody",
                "tr",
                "th",
                "td",
              ],
              ALLOWED_ATTR: ["style", "href", "target", "rel"],
            }),
          }}
        />
        <style>
          {`
          .rich-html p { margin: 0 0 10px; }
          .rich-html h2 { margin: 6px 0 8px; font-size: 20px; }
          .rich-html ul { list-style: disc; padding-left: 1.25rem; margin: 6px 0; }
          .rich-html ol { list-style: decimal; padding-left: 1.25rem; margin: 6px 0; }
          .rich-html a { color: #4ea0ff; text-decoration: underline; }
          `}
        </style>
      </div>

      {/* TABELA DE PONTUAÇÃO */}
      <div style={{ marginBottom: 12 }}>
        <label
          style={{
            display: "block",
            fontWeight: 700,
            margin: "8px 0 6px",
          }}
        >
          Tabela de Pontuação
        </label>

        <div style={{ overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "left",
                    padding: 8,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  APOSTAS
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: 8,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  PONTOS ACERTOS
                </th>
                <th
                  style={{
                    textAlign: "left",
                    padding: 8,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  PONTOS ERROS
                </th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {tabelaPontuacao.map((row, i) => (
                <tr key={i}>
                  <td
                    style={{
                      padding: "6px 8px",
                      borderBottom: "1px solid #f2f2f2",
                    }}
                  >
                    <input
                      value={row.aposta}
                      onChange={(e) => {
                        const v = [...tabelaPontuacao];
                        v[i] = { ...v[i], aposta: e.target.value };
                        setTabelaPontuacao(v);
                      }}
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td
                    style={{
                      padding: "6px 8px",
                      borderBottom: "1px solid #f2f2f2",
                    }}
                  >
                    <input
                      value={row.acerto}
                      onChange={(e) => {
                        const v = [...tabelaPontuacao];
                        v[i] = { ...v[i], acerto: e.target.value };
                        setTabelaPontuacao(v);
                      }}
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td
                    style={{
                      padding: "6px 8px",
                      borderBottom: "1px solid #f2f2f2",
                    }}
                  >
                    <input
                      value={row.erro}
                      onChange={(e) => {
                        const v = [...tabelaPontuacao];
                        v[i] = { ...v[i], erro: e.target.value };
                        setTabelaPontuacao(v);
                      }}
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td style={{ padding: "6px 8px", textAlign: "right" }}>
                    <button
                      onClick={() => {
                        const v = [...tabelaPontuacao];
                        v.splice(i, 1);
                        setTabelaPontuacao(v);
                      }}
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          style={{
            marginTop: 8,
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() =>
              setTabelaPontuacao([
                ...tabelaPontuacao,
                { aposta: "", acerto: "A definir", erro: "A definir" },
              ])
            }
          >
            + Linha
          </button>

          <button onClick={() => setTabelaPontuacao(DEFAULT_TABELA_PONTOS)}>
            Restaurar lista padrão
          </button>

          <button className="btn" onClick={salvar}>
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}
